import {
  createContext,
  useState,
  ReactNode,
  FC,
  useEffect,
  useRef,
} from "react";
import {
  CartItem,
  CartNotice,
  DiscountInfo,
  PromoCodeData,
} from "../types/types";

interface AppState {
  cart: CartItem[];
  addToCart: (item: CartItem, index?: number) => boolean;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  cartNotice: CartNotice | null;
  openCart: (notice: CartNotice) => void;
  closeCart: () => void;
  promoCode: string;
  setPromoCode: (code: string) => void;
  discountInfo: DiscountInfo;
  setDiscountInfo: (info: DiscountInfo) => void;
  appliedPromo: PromoCodeData | null;
  setAppliedPromo: (promo: PromoCodeData | null) => void;
}

export const CartContext = createContext<AppState | undefined>(undefined);

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const hydratedRef = useRef(false);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<PromoCodeData | null>(null);

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartNotice, setCartNotice] = useState<CartNotice | null>(null);
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo>({
    isValid: false,
    discountAmount: 0,
  });

  const addToCart = (item: CartItem, index?: number) => {
    const alreadyInCart = cart.some((cartItem) => cartItem.id === item.id);
    setCart((prevCart) => {
      const itemIndex = prevCart.findIndex(
        (cartItem) => cartItem.id === item.id,
      );
      if (itemIndex > -1) {
        return prevCart;
      }
      if (index === undefined || index >= prevCart.length) {
        return [...prevCart, item];
      }
      const nextCart = [...prevCart];
      nextCart.splice(index, 0, item);
      return nextCart;
    });
    return !alreadyInCart;
  };

  const removeFromCart = (itemId: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== itemId));
  };

  const clearCart = () => {
    setCart([]);
    setPromoCode("");
    setAppliedPromo(null);
    setDiscountInfo({ isValid: false, discountAmount: 0 });
  };

  const openCart = (notice: CartNotice) => {
    setCartNotice(notice);
    setIsCartOpen(true);
  };
  const closeCart = () => {
    setIsCartOpen(false);
    setCartNotice(null);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    clearCart,
    isCartOpen,
    cartNotice,
    openCart,
    closeCart,
    promoCode,
    setPromoCode,
    discountInfo,
    setDiscountInfo,
    appliedPromo,
    setAppliedPromo,
  };

  // The build-time HTML has an empty cart, so the first client render must too
  // (hydration); the first effect pass loads the saved cart instead of
  // persisting, which at that point would overwrite it with [].
  useEffect(() => {
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      const savedCart = localStorage.getItem("shoppingCart");
      if (savedCart) setCart(JSON.parse(savedCart));
      return;
    }
    localStorage.setItem("shoppingCart", JSON.stringify(cart));
  }, [cart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
